import { PropsWithChildren, ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Price } from '@ds';
import { DiamondAnimation, GalaxyButton, Counter } from '@shared';
import styles from './BatchPurchase.module.scss';
import { Discount } from '../discount';

type Props = {
  price: number
  onChangePopupPreset: (quantity: number) => void
  discount?: string;
  title: string
  description?: string
  imgSrc?: string
  imageSlot?: ReactNode
  limitation?: string
  className?: string
  defaultQuantity?: number
  diamondColor?: string
}

const DEFAULT_QUANTITY = 3

export const BatchPurchase = ({ price, onChangePopupPreset, limitation, discount, title, description, imgSrc, imageSlot, className, defaultQuantity, children, diamondColor }: Props & PropsWithChildren) => {
  const [quantity, setQuantity] = useState(defaultQuantity ?? DEFAULT_QUANTITY)
  const { t } = useTranslation()
  const maxQuantity = useMemo(() => {
    if (!limitation) return 12345678;
    const [left, supply] = limitation.split('/');
    const leftNumber = Number(left);
    if (leftNumber && !isNaN(leftNumber)){
      return leftNumber
    };
    return 0;
  },[limitation])
  
  const handleQuantityChange = (quantity: number) => {
      setQuantity(Math.max(0, Math.min(quantity, maxQuantity)))
  }

  const handleBuy = () => {
    onChangePopupPreset(quantity)
  }

  return (
    <div className={`${styles.root}${className ? ` ${className}` : ''}`}>
      {discount && <Discount discount={discount} />}
      <div className={styles.title}>{title}</div>
      {description && <div className={styles.description}>{description}</div>}
      <Counter
        className={styles.counter}
        quantity={quantity}
        blockMessage={!maxQuantity ? t('shop.soldout') : undefined}
        onQuantityChange={handleQuantityChange}
      />
      <GalaxyButton
        withGlow={false}
        className={styles.buyButton}
        onClick={handleBuy}
        disabled={!maxQuantity || !quantity}
      >
          <div className={styles.buyButtonText}>{maxQuantity ? t('shop.buy') : t('shop.soldout')}</div>
          {maxQuantity ?
              <div className={styles.buyButtonPrice}>
                <Price value={quantity * price} />
              </div>
            : null}
          {limitation ? <div className={styles.limitation}>
            <p>
              {limitation}
            </p>
          </div> : null}
      </GalaxyButton>

      <div className={styles.diamondWrapper}>
        <svg viewBox="0 0 199 183" fill="none" className={styles.diamond}>
          <DiamondAnimation optimized diamondColor={diamondColor}/>
        </svg>
        {imageSlot ?? (imgSrc && <img src={imgSrc} className={styles.image} />)}
      </div>
      {children}
    </div>
  )
}
